/* eslint-disable dot-notation */
import React, {
  useRef,
  useCallback,
  useEffect,
  useState,
  useMemo,
} from "react";
import { createContext, useContextSelector } from "use-context-selector";
import { MutationResult, useQuery } from "@apollo/client";
import _ from "lodash";
import Form from "@rjsf/core";
import bluebird from "bluebird";
import { FormInstance } from "../proposals/ProposalForm/types";
import {
  GetFormDefaultsQuery,
  GetFormDefaultsQueryResult,
  MarketplaceFormElementRole,
  MarketplaceOpportunity,
  MarketplaceProposal,
  MarketplaceProposalPhase,
  MarketplaceProposalTemplate,
  Maybe,
  ProfileEntry,
  ProposalLineItem,
  ProposalLineItemInput,
  ProposalMetaItem,
} from "../../schema/generated/schema";
import {
  getMetaFormMap,
  MetaFormMap,
  OverviewFormData,
} from "../proposals/contexts/jsonDataUtil";
import { getOverviewForm } from "../proposals/ProposalMain/utils";
import { formHasErrors, scrollToErrors } from "../FormEditor/util";

export type JsonSchemaDataValue = Pick<
  MarketplaceProposal,
  | "id"
  | "refId"
  | "proposalTemplate"
  | "lineItems"
  | "metaItems"
  | "buyerUserId"
  | "sellerPartnerId"
  | "updatedAt"
  | "subject"
>;

export type JsonSchemaLineItem = ProposalLineItem;
export type JsonSchemaLineItemInput = ProposalLineItemInput;
const genericOverviewForm = getOverviewForm();

export interface JsonSchemaDataContextValue {
  formDefaults: { [key in MarketplaceFormElementRole]?: any };
  valueModel: Maybe<JsonSchemaDataValue>;
  metaForms: MetaFormMap;
  entries: Maybe<readonly ProfileEntry[]>;
  overviewForm: FormInstance<OverviewFormData> | null;
  metaFormReferenceMap: { [formVersionId: string]: Form<any> };
  isPriced: boolean;
  isPricedOpportunity: boolean;
  opportunityHasQuantities: boolean;
  isDirty: boolean;
  subject: string;
  actions: {
    savePendingChanges: () => Promise<boolean>;
    updateIsSeller: (isSeller: boolean) => void;
    updateMetaForm: (
      formVersionId: string,
      partialFormData: Record<string, unknown>
    ) => void;
    updateOverviewForm: (details: OverviewFormData) => void;
  };
}

export interface JsonSchemaActionContextValue {
  proposal?: MarketplaceProposal;
  opportunity?: MarketplaceOpportunity;
  isSaving: boolean;
  actions: {
    refetch: () => void;
    handleLineItemUpsert: (
      item: JsonSchemaLineItemInput
    ) => Promise<MutationResult<JsonSchemaLineItem>>;
    handleLineItemDelete: (itemId: string) => Promise<MutationResult<boolean>>;
    handleMetaItemUpsert: (
      formVersionId: string,
      data: any
    ) => Promise<MutationResult<any>>;
    handleSaveOverview: (
      data: OverviewFormData
    ) => Promise<MutationResult<any>>;
  };
}

export const JsonSchemaActionContext =
  createContext<JsonSchemaActionContextValue>({
    isSaving: false,
    actions: {
      refetch() {
        throw Error("Function not implemented");
      },
      handleLineItemUpsert() {
        throw Error("Function not implemented");
      },
      handleLineItemDelete() {
        throw Error("Function not implemented");
      },
      handleMetaItemUpsert() {
        throw Error("Function not implemented");
      },
      handleSaveOverview() {
        throw Error("Function not implemented");
      },
    },
  });

export const JsonSchemaDataContext = createContext<JsonSchemaDataContextValue>({
  formDefaults: {},
  valueModel: null,
  metaForms: {},
  entries: null,
  overviewForm: null,
  isPriced: false,
  isPricedOpportunity: false,
  opportunityHasQuantities: false,
  isDirty: false,
  metaFormReferenceMap: {},
  subject: "",
  actions: {
    updateIsSeller() {
      throw Error("Function not implemented");
    },
    savePendingChanges() {
      throw Error("Function not implemented");
    },
    updateMetaForm() {
      throw Error("Function not implemented");
    },
    updateOverviewForm() {
      throw Error("Function not implemented");
    },
  },
});

export const JsonSchemaDataContextProvider: React.FC = ({ children }) => {
  const opportunity = useContextSelector(
    JsonSchemaActionContext,
    ctx => ctx?.opportunity
  );
  const proposal = useContextSelector(
    JsonSchemaActionContext,
    ctx => ctx?.proposal
  );
  const actions = useContextSelector(
    JsonSchemaActionContext,
    ctx => ctx?.actions
  );
  const [overviewForm, setOverviewForm] = useState({
    key: genericOverviewForm.versionId,
    dirty: false,
    touchAllErrors: false,
    form: genericOverviewForm,
    formData: {},
    sortOrder: -1,
  } as FormInstance<OverviewFormData>);
  const [metaFormMap, setMetaFormMap] = useState<MetaFormMap>({});
  const metaFormReferenceMap = useRef<{ [key: string]: Form<any> }>({}).current;

  const formDefaultsQuery =
    useQuery<GetFormDefaultsQueryResult>(GetFormDefaultsQuery);

  const valueModel = _.pick(opportunity || proposal, [
    "id",
    "refId",
    "proposalTemplate",
    "lineItems",
    "metaItems",
    "buyerUserId",
    "sellerPartnerId",
    "updatedAt",
    "subject",
  ]) as JsonSchemaDataValue;

  const resetOverviewForm = useCallback(() => {
    const subject = opportunity?.subject || proposal?.subject || undefined;
    const details = _.cloneDeep(
      opportunity?.details || proposal?.details || {}
    ) as any;
    delete details.__typename;
    _.forEach(details, (val, key) => {
      if (val === null) {
        delete details[key];
      }
    });

    if (!overviewForm.dirty) {
      setOverviewForm({
        ...overviewForm,
        formData: {
          ...overviewForm.formData,
          subject,
          ...details,
        },
      });
    }
  }, [overviewForm, valueModel, opportunity, proposal]);

  const subject = overviewForm.formData.subject || "";

  useEffect(() => {
    if (valueModel?.id) {
      const proposalTemplate = (opportunity?.proposalTemplate ||
        proposal?.proposalTemplate) as MarketplaceProposalTemplate;
      const metaItems = (opportunity?.metaItems ||
        proposal?.metaItems) as ProposalMetaItem[];
      let phase = MarketplaceProposalPhase.ProjectPhase;
      if (proposal) {
        phase = MarketplaceProposalPhase.QuotingPhase;
      }
      setMetaFormMap(getMetaFormMap(proposalTemplate, metaItems, phase));
      resetOverviewForm();
    }
  }, [valueModel?.id]);

  const getFormState = (
    formInstance: FormInstance
  ): { dirty: boolean; formData: any; hasErrors: boolean } => {
    const form = metaFormReferenceMap[formInstance.key];
    let hasErrors = false;
    let { formData, dirty } = formInstance;
    if (!form || !form.state) {
      return {
        hasErrors,
        formData,
        dirty,
      };
    }
    hasErrors = formHasErrors(form);
    const { formData: stateFormData } = form.state as any;
    if (!_.isEqual(stateFormData, formData)) {
      // this condition indicates that the form has never
      // been interacted with but that the defaults have somehow been applied
      dirty = true;
      formData = _.cloneDeep(stateFormData);
    }

    return {
      hasErrors,
      formData,
      dirty,
    };
  };

  const saveDetailsForm = async (): Promise<boolean> => {
    const { dirty, formData, hasErrors } = getFormState(overviewForm);
    if (hasErrors) {
      setOverviewForm({
        ...overviewForm,
        touchAllErrors: true,
      });
      return false;
    }

    if (!dirty) {
      return true;
    }

    const success = !!(await actions.handleSaveOverview(formData)).data;
    if (success) {
      setOverviewForm({
        ...overviewForm,
        dirty: false,
      });
    }
    return success;
  };

  const saveMetaFormInstance = async (
    formInstance: FormInstance
  ): Promise<boolean> => {
    const { dirty, formData, hasErrors } = getFormState(formInstance);
    if (hasErrors) {
      // eslint-disable-next-line no-param-reassign
      formInstance.touchAllErrors = true;
      return false;
    }
    if (!dirty) {
      return true;
    }
    try {
      await actions.handleMetaItemUpsert(formInstance.key, formData);
      // eslint-disable-next-line no-param-reassign
      formInstance.dirty = false;
      // eslint-disable-next-line no-param-reassign
      formInstance.formData = formData;
      return true;
    } catch (err) {
      console.error(err);
      // eslint-disable-next-line no-param-reassign
      formInstance.touchAllErrors = true;
      return false;
    }
  };

  const savePendingChanges = useCallback(async () => {
    const detailsSaved = await saveDetailsForm();
    const nextMetaFormMap = _.cloneDeep(metaFormMap);
    const formInstances = Object.values(nextMetaFormMap);
    const results = await bluebird.map(formInstances, saveMetaFormInstance);
    setMetaFormMap(nextMetaFormMap);
    const metaSavedSuccess = results.every(success => success);
    if (!metaSavedSuccess || !detailsSaved) {
      scrollToErrors(Object.values(metaFormReferenceMap));
      return false;
    }
    return true;
  }, [overviewForm, metaFormMap]);

  const isPriced = useMemo(
    () =>
      !!proposal?.lineItems.length &&
      !proposal?.lineItems.some(lineItem => lineItem?.price === 0),
    [proposal?.lineItems]
  );

  const isPricedOpportunity = useMemo(
    () =>
      !!opportunity?.lineItems.length &&
      !opportunity?.lineItems.some(lineItem => lineItem?.price === 0),
    [opportunity?.lineItems]
  );

  const opportunityHasQuantities = useMemo(
    () =>
      !!opportunity?.lineItems.length &&
      !opportunity?.lineItems.some(lineItem => lineItem?.quantity === 0),
    [opportunity?.lineItems]
  );

  let formDefaults = {};
  if (formDefaultsQuery.data) {
    formDefaults = formDefaultsQuery.data?.getFormDefaults.reduce(
      (memo, { role, value }) => {
        return {
          [role]: value,
          ...memo,
        };
      },
      {}
    ) as any;
  }
  const value: JsonSchemaDataContextValue = {
    formDefaults,
    valueModel,
    metaForms: metaFormMap,
    entries: null,
    overviewForm,
    metaFormReferenceMap,
    isPriced,
    isPricedOpportunity,
    opportunityHasQuantities,
    isDirty: overviewForm.dirty || _.some(metaFormMap, form => form.dirty),
    subject,
    actions: {
      savePendingChanges,
      updateIsSeller(isSeller) {
        setOverviewForm({
          ...overviewForm,
          form: getOverviewForm(isSeller),
        });
      },
      updateMetaForm(formVersionId, data) {
        const current = metaFormMap[formVersionId];
        setMetaFormMap({
          ...metaFormMap,
          [formVersionId]: {
            ...current,
            dirty: true,
            formData: data,
          },
        });
        if (
          valueModel.proposalTemplate?.slug.startsWith("vibe") &&
          current.form.name === "RFQ Details"
        ) {
          const overviewFormData = {
            subject: data["title_box"],
            description: data["description_box"],
            dateNeededBy: data["date_needed_by"],
          };
          setOverviewForm({
            ...overviewForm,
            dirty: true,
            formData: {
              ...overviewForm.formData,
              ...overviewFormData,
            } as any,
          });
        }
      },
      updateOverviewForm(data) {
        setOverviewForm({
          ...overviewForm,
          dirty: true,
          formData: data,
        });
      },
    },
  };
  return (
    <JsonSchemaDataContext.Provider value={value}>
      {children}
    </JsonSchemaDataContext.Provider>
  );
};
